import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GeneralV3Response, LoadingType } from "../../../types/CommonTypes";
import { fetchPracticeProductsById, fetchPracticeProductsWithoutDetailsById, fetchPracticeProductsByIdUsingDateOfServiceAndProvider, fetchPracticeProductsListByMfr, fetchPracticeProductsByIdWithModule } from "../../../service/PracticeService";
import { useAppSelector } from "./../redux-hooks";

export type PracticeProductInfo = {
    productId: string;
    discount?: any;
    productName: string;
    discountedPrice: any;
    agreementStatus: string;
    practiceProductId: string;
    billingType: string;
    providerName?: string;
    practiceProviderId?: string
};
export type PracticeAncillaryProductInfo = {
    productId: string;
    productName: string;
    productItemDescription: string;
    sku: string;
    noOfDays?: string|null;
    extendedPrice: string;
    productItemId: string;
    isKit: boolean;
    providerName?: string;
};

export type FetchPracticeProductsParams = {
  practiceId: number;
  dateOfService?: string;
  providerId?: number;
  moduleName?: string;
  includeRxRequiredProducts?: boolean;
  skipLoader?: boolean;
};

type PracticeProductState = {
    practiceProductsInfo?: PracticeProductInfo[] | null;
    practiceAncillaryProductsInfo?: PracticeAncillaryProductInfo[] | null;
    practiceProductsDataFetchStatus?: LoadingType
  };

  const initialState: PracticeProductState = {
    practiceProductsInfo: undefined,
    practiceAncillaryProductsInfo: undefined,
    practiceProductsDataFetchStatus: "idle",
  };

  export const fetchPracticeProductsData = createAsyncThunk("fetchPracticeProductsData", async (practiceId: string) => {
    const response = await fetchPracticeProductsById(practiceId);
    return response.data;
  });
  // CS-1240
  export const fetchPracticeProductsDataWithModule = createAsyncThunk("fetchPracticeProductsDataWithModule",async ({ practiceId, moduleName }: { practiceId: number; moduleName: string }) => {
      const response = await fetchPracticeProductsByIdWithModule(practiceId, moduleName);
      return response.data;
  });
  // 
  export const fetchPracticeProductsDataWithoutDetails = createAsyncThunk("fetchPracticeProductsDataWithoutDetails", async (practiceId: string) => {
    const response = await fetchPracticeProductsWithoutDetailsById(practiceId);
    return response.data;
  });

export const fetchPracticeProductsDataUsingDateOfServiceAndProvider = createAsyncThunk("fetchPracticeProductsDataUsingDateOfServiceAndProvider", async ({ practiceId, dateOfService, providerId ,moduleName, includeRxRequiredProducts, skipLoader }: FetchPracticeProductsParams) => {
    const response = await fetchPracticeProductsByIdUsingDateOfServiceAndProvider(practiceId, dateOfService, providerId ,moduleName, includeRxRequiredProducts, skipLoader ?? false);
    return response.data;
  });
export const  practiceProductListByMfr= createAsyncThunk("practiceProductListByMfr", async (data:any) => {
    const response = await fetchPracticeProductsListByMfr(data);
    return response.data;
  });

  const practiceProductsSlice = createSlice({
    name: "practiceProducts",
    initialState,
    reducers: {
      updatePracticeProductsInfoObject: (state: PracticeProductState, action) => {
        state.practiceProductsInfo = action.payload;
      }
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetchPracticeProductsData.pending, (state) => {
          state.practiceProductsDataFetchStatus = "loading";
        })
        .addCase(fetchPracticeProductsData.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
          state.practiceProductsDataFetchStatus = "success";
          // state.practiceProductsInfo = action.payload.data;
          state.practiceProductsInfo = action.payload?.data; // CS-1984
        })
        .addCase(fetchPracticeProductsData.rejected, (state, action) => {
          state.practiceProductsDataFetchStatus = 'error';
        })
        .addCase(fetchPracticeProductsDataWithoutDetails.pending, (state) => {
          state.practiceProductsDataFetchStatus = "loading";
        })
        .addCase(fetchPracticeProductsDataWithoutDetails.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
          state.practiceProductsDataFetchStatus = "success";
          // state.practiceProductsInfo = action.payload.data;
          state.practiceProductsInfo = action.payload?.data; // CS-1984
        })
        .addCase(fetchPracticeProductsDataWithoutDetails.rejected, (state, action) => {
          state.practiceProductsDataFetchStatus = 'error';
        })
        .addCase(fetchPracticeProductsDataUsingDateOfServiceAndProvider.pending, (state) => {
          state.practiceProductsDataFetchStatus = "loading";
        })
        .addCase(fetchPracticeProductsDataUsingDateOfServiceAndProvider.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
          state.practiceProductsDataFetchStatus = "success";
          // state.practiceProductsInfo = action.payload.data;
          state.practiceProductsInfo = action.payload?.data; // CS-1984
        })
        .addCase(fetchPracticeProductsDataUsingDateOfServiceAndProvider.rejected, (state, action) => {
          state.practiceProductsDataFetchStatus = 'error';
        })
        .addCase(fetchPracticeProductsDataWithModule.pending, (state) => {
          state.practiceProductsDataFetchStatus = "loading";
        })
        .addCase(fetchPracticeProductsDataWithModule.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
          state.practiceProductsDataFetchStatus = "success";
          // state.practiceProductsInfo = action.payload.data;
          state.practiceProductsInfo = action.payload?.data; // CS-1984
        })
        .addCase(fetchPracticeProductsDataWithModule.rejected, (state, action) => {
          state.practiceProductsDataFetchStatus = 'error';
        })
        .addCase(practiceProductListByMfr.pending, (state) => {
          state.practiceProductsDataFetchStatus = "loading";
        })
        .addCase(practiceProductListByMfr.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
          state.practiceProductsDataFetchStatus = "success";
          // state.practiceAncillaryProductsInfo = action.payload.data;
          state.practiceAncillaryProductsInfo = action.payload?.data; // CS-1984
        })
        .addCase(practiceProductListByMfr.rejected, (state, action) => {
          state.practiceProductsDataFetchStatus = 'error';
        });
    },
  });

export const { updatePracticeProductsInfoObject } = practiceProductsSlice.actions;
export const usePracticeProductsSlice = () => useAppSelector((state) => state.practiceProductsSlice);
export default practiceProductsSlice.reducer;